/**
 * Shared filter definitions for Pro mobile list pages.
 * Labels are i18n keys under the `pro` namespace — resolve with t() at call site.
 */

export interface ProFilterOptionDef {
  value: string;
  labelKey?: string;
  /** Used when labelKey is null (e.g. dynamic session year). */
  labelValue?: string;
}

export interface ProFilterDefSource {
  key: string;
  labelKey: string;
  options: ProFilterOptionDef[];
}

export const PRO_CHAMBER_OPTIONS: ProFilterOptionDef[] = [
  { value: '', labelKey: 'filters.allChambers' },
  { value: 'House', labelKey: 'filters.house' },
  { value: 'Senate', labelKey: 'filters.senate' },
];

/*
 * PRO_CURRENT_SESSION was a hardcoded '2026' — frozen, so it would have gone on
 * claiming 2026 through the whole 2027 session. The session filter's options are
 * now supplied by the caller from useSessionOptions(); this module keeps only the
 * static, non-temporal filter definitions.
 */

export const PRO_BILL_FILTER_DEFS: ProFilterDefSource[] = [
  {
    key: 'chamber',
    labelKey: 'filters.chamber',
    options: PRO_CHAMBER_OPTIONS,
  },
  {
    key: 'bill_status',
    labelKey: 'filters.status',
    options: [
      { value: '', labelKey: 'filters.allStatuses' },
      { value: 'Introduced', labelKey: 'filters.introduced' },
      { value: 'In Committee', labelKey: 'filters.inCommittee' },
      { value: 'Passed', labelKey: 'filters.passed' },
      { value: 'Failed', labelKey: 'filters.failed' },
      { value: 'Signed', labelKey: 'filters.signed' },
    ],
  },
  {
    key: 'session',
    labelKey: 'filters.session',
    // Only the static "all" entry lives here. Registered sessions are appended at
    // render time from useSessionOptions() — a literal list here could not reach
    // more than the sessions someone remembered to type.
    options: [{ value: '', labelKey: 'filters.allSessions' }],
  },
];

export const PRO_BILL_FILTER_KEYS = ['chamber', 'bill_status', 'session'] as const;

export const PRO_BILL_DEFAULT_FILTERS: Record<string, string> = {
  chamber: '',
  bill_status: '',
  session: '',
};

export const PRO_LEGISLATOR_FILTER_DEFS: ProFilterDefSource[] = [
  {
    key: 'chamber',
    labelKey: 'filters.chamber',
    options: PRO_CHAMBER_OPTIONS,
  },
  {
    key: 'party',
    labelKey: 'filters.party',
    options: [
      { value: '', labelKey: 'filters.allParties' },
      { value: 'Democrat', labelKey: 'filters.democrat' },
      { value: 'Republican', labelKey: 'filters.republican' },
    ],
  },
];

export const PRO_LEGISLATOR_FILTER_KEYS = ['chamber', 'party'] as const;

export const PRO_LEGISLATOR_DEFAULT_FILTERS: Record<string, string> = {
  chamber: '',
  party: '',
};

export const PRO_CALENDAR_FILTER_DEFS: ProFilterDefSource[] = [
  {
    key: 'chamber',
    labelKey: 'filters.chamber',
    options: PRO_CHAMBER_OPTIONS,
  },
];


/**
 * Append registry session options onto the static session filter def.
 * Keeps the leading "All Sessions" entry (value '') so clearing stays explicit
 * and matches ProFilterChips inactive-filter semantics. Drops any 'all' sentinel
 * from useSessionOptions — Pro uses '' for the unscoped choice.
 */
export function withSessionFilterOptions(
  defs: ProFilterDefSource[],
  sessionOptions: Array<{ value: string; label: string }>,
): ProFilterDefSource[] {
  const registryOptions: ProFilterOptionDef[] = sessionOptions
    .filter((o) => o.value && o.value !== 'all')
    .map((o) => ({ value: o.value, labelValue: o.label }));

  return defs.map((def) => {
    if (def.key !== 'session') return def;
    const allEntry = def.options.filter((o) => o.value === '');
    return {
      ...def,
      options: [...allEntry, ...registryOptions],
    };
  });
}

/** Resolve filter defs to ProFilterChips shape using t(). */
export function resolveProFilterDefs(
  defs: ProFilterDefSource[],
  t: (key: string) => string,
): Array<{ key: string; label: string; options: Array<{ value: string; label: string }> }> {
  return defs.map((f) => ({
    key: f.key,
    label: t(f.labelKey),
    options: f.options.map((o) => ({
      value: o.value,
      label: o.labelKey ? t(o.labelKey) : (o.labelValue ?? o.value),
    })),
  }));
}
